import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import EgresosIngresosService from "../../services/EgresosIngresosService";
import ReembolsoService from "../../services/ReembolsoService";
import CajaService from "../../services/CajaService";
import TelefonosService from "../../services/TelefonosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Grid,
  TextField,
  Paper,
  Typography,
  Stack,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const IngresosSucursales = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [reembolsosList, setReembolsosList] = useState([]);
  const [egresosIngresosList, setEgresosIngresosList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros de fecha
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Ingresos en Sucursales";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const formatDateToYYYYMMDD = (dt) => {
    if (!dt) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resEI] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        EgresosIngresosService.getEgresosIngresos().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaEI = Array.isArray(resEI?.data?.egresosIngresos)
        ? resEI.data.egresosIngresos
        : Array.isArray(resEI?.data)
        ? resEI.data
        : [];

      // Obtención limpia de reembolsos de BD
      const rawReembolsos = [];

      try {
        const resGeneral = await ReembolsoService.getReembolsos();
        const payload = resGeneral?.data?.reembolsos ?? resGeneral?.data;
        if (Array.isArray(payload)) rawReembolsos.push(...payload);
      } catch (e) {}

      if (listaSuc.length > 0) {
        try {
          const reemProms = listaSuc.map((suc) =>
            ReembolsoService.getReembolsoActual(suc.idSucursal).catch(() => null)
          );
          const reemResps = await Promise.all(reemProms);

          reemResps.forEach((res, idx) => {
            const sucId = listaSuc[idx]?.idSucursal;
            const payload = res?.data?.reembolsos ?? res?.data;
            const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push({
                  ...r,
                  idSucursal: r.idSucursal ?? r.sucursalId ?? sucId,
                });
              }
            });
          });
        } catch (e) {}
      }

      try {
        const resCortes = await CajaService.getCortes().catch(() => null);
        const cortesList = Array.isArray(resCortes?.data) ? resCortes.data : Array.isArray(resCortes) ? resCortes : [];
        if (cortesList.length > 0) {
          const cortesProms = cortesList.map((c) =>
            ReembolsoService.getReembolsoPorCorte(c.idSucursal || c.sucursalId, c.idCorte || c.idCorteReembolso).catch(() => null)
          );
          const cortesResps = await Promise.all(cortesProms);
          cortesResps.forEach((res) => {
            const payload = res?.reembolsos ?? res?.data?.reembolsos ?? res;
            const arr = Array.isArray(payload) ? payload : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push(r);
              }
            });
          });
        }
      } catch (e) {}

      const seenKeys = new Set();
      const listaFinalReembolsos = [];

      rawReembolsos.forEach((r) => {
        const key = r.idReembolso
          ? `id:${r.idReembolso}`
          : `${r.idSucursal || r.sucursalId}-${r.fecha}-${r.monto || r.cantidad}-${r.motivo || ""}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          listaFinalReembolsos.push(r);
        }
      });

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setEgresosIngresosList(listaEI);
      setReembolsosList(listaFinalReembolsos);
    } catch (error) {
      console.error("Error cargando datos para Ingresos en Sucursales:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar los ingresos en sucursales del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const coincideSucursal = (item, sucObj) => {
    if (!item || !sucObj) return false;
    const idSucItem = String(item.idSucursal || item.sucursalId || item.sucursal || "");
    const nomSucItem = String(item.nombreSucursal || item.sucursal || "").trim().toLowerCase();
    const targetId = String(sucObj.idSucursal);
    const targetNom = String(sucObj.nombre).trim().toLowerCase();

    return idSucItem === targetId || nomSucItem === targetNom;
  };

  const getDesglosePagoSolicitud = (sol) => {
    const totalSol = Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const adeudo = Number(sol.saldoPendiente ?? sol.adeudo ?? 0);
    const descuento = Number(sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const pagado = Number(sol.pagado ?? (totalSol - adeudo > 0 ? totalSol - adeudo : 0));

    let ef = 0,
      tj = 0,
      ch = 0,
      tr = 0;

    const forma = `${sol.formaPago || sol.metodoPago || sol.tipoPago || ""}`.toLowerCase();

    if (forma.includes("tarjeta")) {
      tj = pagado;
    } else if (forma.includes("cheque")) {
      ch = pagado;
    } else if (forma.includes("transferencia") || forma.includes("transf")) {
      tr = pagado;
    } else {
      ef = pagado;
    }

    return {
      efectivo: ef,
      tarjeta: tj,
      cheque: ch,
      transferencia: tr,
      descuento: descuento,
      adeudo: adeudo,
      total: totalSol,
    };
  };

  // Generar datos agrupados por DÍA y por SUCURSAL
  const datosPorDiaYSucursal = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
    if (!start || !end) return [];

    const datesMap = new Map();

    let cur = new Date(start);
    while (cur.getTime() <= end.getTime()) {
      const dateStr = formatDateToYYYYMMDD(cur);
      datesMap.set(dateStr, []);
      cur.setDate(cur.getDate() + 1);
    }

    const resultList = [];

    datesMap.forEach((_, diaStr) => {
      const sucursalesDiaData = [];
      let totalMovimientosDia = 0;

      sucursales.forEach((suc) => {
        const solsSuc = solicitudes.filter((sol) => {
          const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
          if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
            return false;
          }
          if (!coincideSucursal(sol, suc)) return false;
          const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
          return dt && formatDateToYYYYMMDD(dt) === diaStr;
        });

        const reemsSuc = reembolsosList.filter((r) => {
          if (!coincideSucursal(r, suc)) return false;
          const dt = parseFechaDate(r.fecha || r.created_at || r.fechaReembolso || r.createdAt);
          return dt && formatDateToYYYYMMDD(dt) === diaStr;
        });

        const egresosSuc = egresosIngresosList.filter((e) => {
          const tipo = `${e.operacion ?? e.tipo ?? e.tipoMovimiento ?? ""}`.toLowerCase();
          const esEgreso = tipo.includes("egreso") || tipo.includes("gasto");
          if (!esEgreso) return false;
          if (!coincideSucursal(e, suc)) return false;
          const dt = parseFechaDate(e.fecha || e.created_at);
          return dt && formatDateToYYYYMMDD(dt) === diaStr;
        });

        const ingresosSuc = egresosIngresosList.filter((i) => {
          const tipo = `${i.operacion ?? i.tipo ?? i.tipoMovimiento ?? ""}`.toLowerCase();
          const esIngreso = tipo.includes("ingreso") || tipo.includes("entrada");
          if (!esIngreso) return false;
          if (!coincideSucursal(i, suc)) return false;
          const dt = parseFechaDate(i.fecha || i.created_at);
          return dt && formatDateToYYYYMMDD(dt) === diaStr;
        });

        let descuentos = 0;
        let efectivoNeto = 0;
        let tarjeta = 0;
        let cheque = 0;
        let transferencia = 0;

        solsSuc.forEach((sol) => {
          const d = getDesglosePagoSolicitud(sol);
          descuentos += d.descuento;
          efectivoNeto += d.efectivo;
          tarjeta += d.tarjeta;
          cheque += d.cheque;
          transferencia += d.transferencia;
        });

        const reembolsos = reemsSuc.reduce((acc, r) => acc + Number(r.monto || r.cantidad || 0), 0);
        const gastos = egresosSuc.reduce((acc, g) => acc + Number(g.monto || g.cantidad || 0), 0);
        const otrosIngresos = ingresosSuc.reduce((acc, ing) => acc + Number(ing.monto || ing.cantidad || 0), 0);

        const efectivoEN_R = efectivoNeto - reembolsos;
        const efectivoEN_R_G_I = efectivoNeto - reembolsos - gastos + otrosIngresos;
        const totalNeto = efectivoEN_R_G_I + tarjeta + cheque + transferencia;

        const tieneActividad =
          solsSuc.length > 0 ||
          reemsSuc.length > 0 ||
          egresosSuc.length > 0 ||
          ingresosSuc.length > 0 ||
          Math.abs(totalNeto) > 0.001;

        if (tieneActividad) {
          totalMovimientosDia += 1;
        }

        sucursalesDiaData.push({
          sucursalObj: suc,
          nombreSucursal: suc.nombre,
          descuentos,
          reembolsos,
          otrosIngresos,
          gastos,
          efectivoNeto,
          efectivoEN_R,
          efectivoEN_R_G_I,
          tarjeta,
          cheque,
          transferencia,
          totalNeto,
          tieneActividad,
        });
      });

      if (totalMovimientosDia > 0) {
        resultList.push({
          fechaStr: diaStr,
          sucursalesData: sucursalesDiaData,
        });
      }
    });

    return resultList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursales, reembolsosList, egresosIngresosList, fechaDe, fechaA]);

  // Concentrado Acumulado para la tabla de "Suma total solicitudes" al final
  const concentradoTotalAcumulado = useMemo(() => {
    const sucursalesTotales = sucursales.map((suc) => {
      let descuentos = 0;
      let reembolsos = 0;
      let otrosIngresos = 0;
      let gastos = 0;
      let efectivoNeto = 0;
      let tarjeta = 0;
      let cheque = 0;
      let transferencia = 0;

      datosPorDiaYSucursal.forEach((diaItem) => {
        const sucRow = diaItem.sucursalesData.find((s) => String(s.sucursalObj.idSucursal) === String(suc.idSucursal));
        if (sucRow) {
          descuentos += sucRow.descuentos;
          reembolsos += sucRow.reembolsos;
          otrosIngresos += sucRow.otrosIngresos;
          gastos += sucRow.gastos;
          efectivoNeto += sucRow.efectivoNeto;
          tarjeta += sucRow.tarjeta;
          cheque += sucRow.cheque;
          transferencia += sucRow.transferencia;
        }
      });

      const efectivoEN_R = efectivoNeto - reembolsos;
      const efectivoEN_R_G_I = efectivoNeto - reembolsos - gastos + otrosIngresos;
      const totalNeto = efectivoEN_R_G_I + tarjeta + cheque + transferencia;

      return {
        sucursalObj: suc,
        nombreSucursal: suc.nombre,
        descuentos,
        reembolsos,
        otrosIngresos,
        gastos,
        efectivoNeto,
        efectivoEN_R,
        efectivoEN_R_G_I,
        tarjeta,
        cheque,
        transferencia,
        totalNeto,
      };
    });

    const granTotal = sucursalesTotales.reduce(
      (acc, s) => ({
        descuentos: acc.descuentos + s.descuentos,
        reembolsos: acc.reembolsos + s.reembolsos,
        otrosIngresos: acc.otrosIngresos + s.otrosIngresos,
        gastos: acc.gastos + s.gastos,
        efectivoNeto: acc.efectivoNeto + s.efectivoNeto,
        efectivoEN_R: acc.efectivoEN_R + s.efectivoEN_R,
        efectivoEN_R_G_I: acc.efectivoEN_R_G_I + s.efectivoEN_R_G_I,
        tarjeta: acc.tarjeta + s.tarjeta,
        cheque: acc.cheque + s.cheque,
        transferencia: acc.transferencia + s.transferencia,
        totalNeto: acc.totalNeto + s.totalNeto,
      }),
      {
        descuentos: 0,
        reembolsos: 0,
        otrosIngresos: 0,
        gastos: 0,
        efectivoNeto: 0,
        efectivoEN_R: 0,
        efectivoEN_R_G_I: 0,
        tarjeta: 0,
        cheque: 0,
        transferencia: 0,
        totalNeto: 0,
      }
    );

    return { sucursalesTotales, granTotal };
  }, [sucursales, datosPorDiaYSucursal]);

  // Exportación a PDF Institucional
  const handleExportarPDF = async () => {
    if (datosPorDiaYSucursal.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen registros con movimientos en el rango de fechas seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) {}
    }

    const sucursalNomText = (sucursalActualObj.nombre || "SUCURSAL").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) {}

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos de la sucursal debajo del recuadro de informes
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título y Período
    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text("INGRESOS EN SUCURSALES", 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 4);

    let nextY = currY + 10;

    // 1. Tablas Diarias por Movimiento
    datosPorDiaYSucursal.forEach((diaItem) => {
      const subtotalDia = diaItem.sucursalesData.reduce(
        (acc, s) => ({
          descuentos: acc.descuentos + s.descuentos,
          reembolsos: acc.reembolsos + s.reembolsos,
          otrosIngresos: acc.otrosIngresos + s.otrosIngresos,
          gastos: acc.gastos + s.gastos,
          efectivoNeto: acc.efectivoNeto + s.efectivoNeto,
          efectivoEN_R: acc.efectivoEN_R + s.efectivoEN_R,
          efectivoEN_R_G_I: acc.efectivoEN_R_G_I + s.efectivoEN_R_G_I,
          tarjeta: acc.tarjeta + s.tarjeta,
          cheque: acc.cheque + s.cheque,
          transferencia: acc.transferencia + s.transferencia,
          totalNeto: acc.totalNeto + s.totalNeto,
        }),
        {
          descuentos: 0,
          reembolsos: 0,
          otrosIngresos: 0,
          gastos: 0,
          efectivoNeto: 0,
          efectivoEN_R: 0,
          efectivoEN_R_G_I: 0,
          tarjeta: 0,
          cheque: 0,
          transferencia: 0,
          totalNeto: 0,
        }
      );

      const filasSucursales = diaItem.sucursalesData.map((sucRow) => [
        sucRow.nombreSucursal,
        `$${sucRow.descuentos.toFixed(2)}`,
        `$${sucRow.reembolsos.toFixed(2)}`,
        `$${sucRow.otrosIngresos.toFixed(2)}`,
        `$${sucRow.gastos.toFixed(2)}`,
        `$${sucRow.efectivoNeto.toFixed(2)}`,
        `$${sucRow.efectivoEN_R.toFixed(2)}`,
        `$${sucRow.efectivoEN_R_G_I.toFixed(2)}`,
        `$${sucRow.tarjeta.toFixed(2)}`,
        `$${sucRow.cheque.toFixed(2)}`,
        `$${sucRow.transferencia.toFixed(2)}`,
        `$${sucRow.totalNeto.toFixed(2)}`,
      ]);

      const filaSubtotal = [
        "Total del Día:",
        `$${subtotalDia.descuentos.toFixed(2)}`,
        `$${subtotalDia.reembolsos.toFixed(2)}`,
        `$${subtotalDia.otrosIngresos.toFixed(2)}`,
        `$${subtotalDia.gastos.toFixed(2)}`,
        `$${subtotalDia.efectivoNeto.toFixed(2)}`,
        `$${subtotalDia.efectivoEN_R.toFixed(2)}`,
        `$${subtotalDia.efectivoEN_R_G_I.toFixed(2)}`,
        `$${subtotalDia.tarjeta.toFixed(2)}`,
        `$${subtotalDia.cheque.toFixed(2)}`,
        `$${subtotalDia.transferencia.toFixed(2)}`,
        `$${subtotalDia.totalNeto.toFixed(2)}`,
      ];

      autoTable(doc, {
        startY: nextY,
        head: [
          [
            {
              content: diaItem.fechaStr,
              colSpan: 12,
              styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" },
            },
          ],
          [
            { content: "Sucursal", rowSpan: 2 },
            { content: "Descuentos", rowSpan: 2 },
            { content: "Reembolsos", rowSpan: 2 },
            { content: "Otros ingresos", rowSpan: 2 },
            { content: "Gastos", rowSpan: 2 },
            { content: "Solicitudes", colSpan: 6, styles: { halign: "center" } },
            { content: "Total Neto", rowSpan: 2 },
          ],
          [
            "EN",
            "EN-R",
            "EN-R-G+I",
            "Tarjeta",
            "Cheque",
            "Transf",
          ],
        ],
        body: [...filasSucursales, filaSubtotal],
        headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
        styles: { fontSize: 6.5, cellPadding: 1.5, halign: "center" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
      });

      nextY = doc.lastAutoTable.finalY + 8;
    });

    // 2. Tabla Concentrada: Suma total solicitudes
    const filasConcentrado = concentradoTotalAcumulado.sucursalesTotales.map((sucRow) => [
      sucRow.nombreSucursal,
      `$${sucRow.descuentos.toFixed(2)}`,
      `$${sucRow.reembolsos.toFixed(2)}`,
      `$${sucRow.otrosIngresos.toFixed(2)}`,
      `$${sucRow.gastos.toFixed(2)}`,
      `$${sucRow.efectivoNeto.toFixed(2)}`,
      `$${sucRow.efectivoEN_R.toFixed(2)}`,
      `$${sucRow.efectivoEN_R_G_I.toFixed(2)}`,
      `$${sucRow.tarjeta.toFixed(2)}`,
      `$${sucRow.cheque.toFixed(2)}`,
      `$${sucRow.transferencia.toFixed(2)}`,
      `$${sucRow.totalNeto.toFixed(2)}`,
    ]);

    const granTotal = concentradoTotalAcumulado.granTotal;
    const filaGranTotal = [
      "GRAN TOTAL ACUMULADO:",
      `$${granTotal.descuentos.toFixed(2)}`,
      `$${granTotal.reembolsos.toFixed(2)}`,
      `$${granTotal.otrosIngresos.toFixed(2)}`,
      `$${granTotal.gastos.toFixed(2)}`,
      `$${granTotal.efectivoNeto.toFixed(2)}`,
      `$${granTotal.efectivoEN_R.toFixed(2)}`,
      `$${granTotal.efectivoEN_R_G_I.toFixed(2)}`,
      `$${granTotal.tarjeta.toFixed(2)}`,
      `$${granTotal.cheque.toFixed(2)}`,
      `$${granTotal.transferencia.toFixed(2)}`,
      `$${granTotal.totalNeto.toFixed(2)}`,
    ];

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          {
            content: "Suma total solicitudes",
            colSpan: 12,
            styles: { halign: "center", fillColor: [40, 53, 147], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
          },
        ],
        [
          { content: "Sucursal", rowSpan: 2 },
          { content: "Descuentos", rowSpan: 2 },
          { content: "Reembolsos", rowSpan: 2 },
          { content: "Otros ingresos", rowSpan: 2 },
          { content: "Gastos", rowSpan: 2 },
          { content: "Solicitudes", colSpan: 6, styles: { halign: "center" } },
          { content: "Total Neto", rowSpan: 2 },
        ],
        [
          "EN",
          "EN-R",
          "EN-R-G+I",
          "Tarjeta",
          "Cheque",
          "Transf",
        ],
      ],
      body: [...filasConcentrado, filaGranTotal],
      headStyles: { fillColor: [48, 63, 159], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
      styles: { fontSize: 6.5, cellPadding: 1.5, halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    });

    // Pie de Página en cada hoja
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Ingresos_Sucursales_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Ingresos de Sucursales en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  INGRESOS EN SUCURSALES
                </Typography>
              </Box>

              {/* Filtros de Rango de Fechas */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.1rem" }}>
                        Selecciona un rango de fechas
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center">
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setFechaDe(getFechaHoy());
                              setFechaA(getFechaHoy());
                            }}
                            startIcon={<RefreshIcon />}
                            sx={{ backgroundColor: "#18234f", color: "white", ml: 1 }}
                          >
                            Restablecer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tablas Diarias e Indicador de Carga */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : datosPorDiaYSucursal.length === 0 ? (
                <Box
                  sx={{
                    width: "95%",
                    maxWidth: "1200px",
                    textAlign: "center",
                    py: 4,
                    mb: 3,
                    backgroundColor: "#fff8e1",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: "#b78103", fontWeight: "bold" }}>
                    No existen registros con movimientos para el rango de fechas seleccionado ({fechaDe} al {fechaA}).
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Tablas de cada Día con Cantidades */}
                  {datosPorDiaYSucursal.map((diaItem) => {
                    const subtotalDia = diaItem.sucursalesData.reduce(
                      (acc, s) => ({
                        descuentos: acc.descuentos + s.descuentos,
                        reembolsos: acc.reembolsos + s.reembolsos,
                        otrosIngresos: acc.otrosIngresos + s.otrosIngresos,
                        gastos: acc.gastos + s.gastos,
                        efectivoNeto: acc.efectivoNeto + s.efectivoNeto,
                        efectivoEN_R: acc.efectivoEN_R + s.efectivoEN_R,
                        efectivoEN_R_G_I: acc.efectivoEN_R_G_I + s.efectivoEN_R_G_I,
                        tarjeta: acc.tarjeta + s.tarjeta,
                        cheque: acc.cheque + s.cheque,
                        transferencia: acc.transferencia + s.transferencia,
                        totalNeto: acc.totalNeto + s.totalNeto,
                      }),
                      {
                        descuentos: 0,
                        reembolsos: 0,
                        otrosIngresos: 0,
                        gastos: 0,
                        efectivoNeto: 0,
                        efectivoEN_R: 0,
                        efectivoEN_R_G_I: 0,
                        tarjeta: 0,
                        cheque: 0,
                        transferencia: 0,
                        totalNeto: 0,
                      }
                    );

                    return (
                      <Box key={diaItem.fechaStr} sx={{ width: "95%", maxWidth: "1200px", mb: 5 }}>
                        <Table
                          sx={{
                            width: "100%",
                            backgroundColor: "white",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            overflow: "hidden",
                            "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                          }}
                        >
                          <TableHead>
                            {/* Fila 1: Fecha */}
                            <TableRow sx={{ backgroundColor: "#18234f" }}>
                              <TableCell
                                colSpan={12}
                                align="center"
                                sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}
                              >
                                {diaItem.fechaStr}
                              </TableCell>
                            </TableRow>

                            {/* Fila 2: Cabeceras principales */}
                            <TableRow sx={{ backgroundColor: "#006787" }}>
                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Sucursal
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Descuentos
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Reembolsos
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Otros ingresos
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Gastos
                              </TableCell>

                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{
                                  color: "white",
                                  fontWeight: "bold",
                                  borderBottom: "1px solid #ffffff40",
                                }}
                              >
                                Solicitudes
                              </TableCell>

                              <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                Total Neto
                              </TableCell>
                            </TableRow>

                            {/* Fila 3: Subcolumnas */}
                            <TableRow sx={{ backgroundColor: "#014e65" }}>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Efectivo Neto (EN)
                              </TableCell>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Efectivo (EN-R)
                              </TableCell>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Efectivo (EN-R-G+I)
                              </TableCell>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Tarjeta
                              </TableCell>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Cheque
                              </TableCell>
                              <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                                Transf
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {diaItem.sucursalesData.map((sucRow) => (
                              <TableRow key={sucRow.nombreSucursal} sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                <TableCell sx={{ fontWeight: "bold" }}>{sucRow.nombreSucursal}</TableCell>
                                <TableCell align="center" sx={{ color: "#d32f2f" }}>
                                  ${sucRow.descuentos.toFixed(2)}
                                </TableCell>
                                <TableCell align="center" sx={{ color: "#d32f2f" }}>
                                  ${sucRow.reembolsos.toFixed(2)}
                                </TableCell>
                                <TableCell align="center" sx={{ color: "#2e7d32" }}>
                                  ${sucRow.otrosIngresos.toFixed(2)}
                                </TableCell>
                                <TableCell align="center" sx={{ color: "#d32f2f" }}>
                                  ${sucRow.gastos.toFixed(2)}
                                </TableCell>

                                <TableCell align="center">${sucRow.efectivoNeto.toFixed(2)}</TableCell>
                                <TableCell align="center" sx={{ backgroundColor: "#fafafa" }}>
                                  ${sucRow.efectivoEN_R.toFixed(2)}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#eef2f6" }}>
                                  ${sucRow.efectivoEN_R_G_I.toFixed(2)}
                                </TableCell>
                                <TableCell align="center">${sucRow.tarjeta.toFixed(2)}</TableCell>
                                <TableCell align="center">${sucRow.cheque.toFixed(2)}</TableCell>
                                <TableCell align="center">${sucRow.transferencia.toFixed(2)}</TableCell>

                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold", color: "#1b5e20", backgroundColor: "#e8f5e9" }}
                                >
                                  ${sucRow.totalNeto.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}

                            <TableRow sx={{ backgroundColor: "#e0f2f1", borderTop: "2px solid #006787" }}>
                              <TableCell sx={{ fontWeight: "bold" }}>Total del Día:</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${subtotalDia.descuentos.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${subtotalDia.reembolsos.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                                ${subtotalDia.otrosIngresos.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${subtotalDia.gastos.toFixed(2)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.efectivoNeto.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.efectivoEN_R.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.efectivoEN_R_G_I.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.tarjeta.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.cheque.toFixed(2)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                ${subtotalDia.transferencia.toFixed(2)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#1b5e20" }}>
                                ${subtotalDia.totalNeto.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })}

                  {/* Tabla Concentrada final: Suma total solicitudes */}
                  <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4, mt: 2 }}>
                    <Table
                      sx={{
                        width: "100%",
                        backgroundColor: "white",
                        border: "2px solid #283593",
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 4px 12px rgba(40, 53, 147, 0.15)",
                        "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                      }}
                    >
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#283593" }}>
                          <TableCell
                            colSpan={12}
                            align="center"
                            sx={{ color: "white", fontWeight: "bold", fontSize: "1.1rem" }}
                          >
                            Suma total solicitudes
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: "#303f9f" }}>
                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Sucursal
                          </TableCell>
                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Descuentos
                          </TableCell>
                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Reembolsos
                          </TableCell>
                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Otros ingresos
                          </TableCell>
                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Gastos
                          </TableCell>

                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{
                              color: "white",
                              fontWeight: "bold",
                              borderBottom: "1px solid #ffffff40",
                            }}
                          >
                            Solicitudes
                          </TableCell>

                          <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Total Neto
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: "#3f51b5" }}>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Efectivo Neto (EN)
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Efectivo (EN-R)
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Efectivo (EN-R-G+I)
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Tarjeta
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Cheque
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.78rem" }}>
                            Transf
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {concentradoTotalAcumulado.sucursalesTotales.map((sucRow) => (
                          <TableRow key={sucRow.nombreSucursal} sx={{ "&:hover": { backgroundColor: "#f3e5f5" } }}>
                            <TableCell sx={{ fontWeight: "bold" }}>{sucRow.nombreSucursal}</TableCell>
                            <TableCell align="center" sx={{ color: "#d32f2f" }}>
                              ${sucRow.descuentos.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ color: "#d32f2f" }}>
                              ${sucRow.reembolsos.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ color: "#2e7d32" }}>
                              ${sucRow.otrosIngresos.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ color: "#d32f2f" }}>
                              ${sucRow.gastos.toFixed(2)}
                            </TableCell>

                            <TableCell align="center">${sucRow.efectivoNeto.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ backgroundColor: "#fafafa" }}>
                              ${sucRow.efectivoEN_R.toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#ede7f6" }}>
                              ${sucRow.efectivoEN_R_G_I.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">${sucRow.tarjeta.toFixed(2)}</TableCell>
                            <TableCell align="center">${sucRow.cheque.toFixed(2)}</TableCell>
                            <TableCell align="center">${sucRow.transferencia.toFixed(2)}</TableCell>

                            <TableCell
                              align="center"
                              sx={{ fontWeight: "bold", color: "#1b5e20", backgroundColor: "#e8f5e9" }}
                            >
                              ${sucRow.totalNeto.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}

                        <TableRow sx={{ backgroundColor: "#c5cae9", borderTop: "2px solid #283593" }}>
                          <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: "#1a237e" }}>
                            GRAN TOTAL ACUMULADO:
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                            ${concentradoTotalAcumulado.granTotal.descuentos.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                            ${concentradoTotalAcumulado.granTotal.reembolsos.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                            ${concentradoTotalAcumulado.granTotal.otrosIngresos.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                            ${concentradoTotalAcumulado.granTotal.gastos.toFixed(2)}
                          </TableCell>

                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            ${concentradoTotalAcumulado.granTotal.efectivoNeto.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            ${concentradoTotalAcumulado.granTotal.efectivoEN_R.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#1a237e" }}>
                            ${concentradoTotalAcumulado.granTotal.efectivoEN_R_G_I.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            ${concentradoTotalAcumulado.granTotal.tarjeta.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            ${concentradoTotalAcumulado.granTotal.cheque.toFixed(2)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            ${concentradoTotalAcumulado.granTotal.transferencia.toFixed(2)}
                          </TableCell>

                          <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.95rem", color: "#1b5e20" }}>
                            ${concentradoTotalAcumulado.granTotal.totalNeto.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón flotante lateral derecho de exportación a PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default IngresosSucursales;
